import Autocomplete from '@zenbusiness/zui/Autocomplete';
import Checkbox from '@zenbusiness/zui/Checkbox';
import FormControl from '@zenbusiness/zui/FormControl';
import FormControlLabel from '@zenbusiness/zui/FormControlLabel';
import Radio from '@zenbusiness/zui/Radio';
import RadioGroup from '@zenbusiness/zui/RadioGroup';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import TextField from '@zenbusiness/zui/TextField';
import PropTypes from 'prop-types';

import {
  INDUSTRY_CATEGORY_LIST,
  INDUSTRY_SUBCATEGORY_MAP
} from '#client/components/OpenAccountApplication/components/CustomFields/constants/verticalList';
import {
  ENTITY_TYPE_OPTIONS,
  SOURCE_OF_FUNDS_OPTIONS,
  ACCOUNT_PURPOSE_OPTIONS,
  TRANSACTION_VOLUME_OPTIONS,
  US_NEXUS_OPTIONS,
  UNIT_TEXT_DETAIL_MAX_LENGTH
} from '#client/constants/additionalInfo';

import { RadioCardStyled } from '../AdditionalInfoModal.styles';
import { QuestionWrapper } from './QuestionWrapper';

// Entity Type Field
export const EntityTypeField = ({ value, onChange }) => (
  <QuestionWrapper questionText="What is your business's legal entity type?">
    <Autocomplete
      getOptionLabel={(option) => option.label || ''}
      onChange={(event, newValue) => onChange(newValue?.value || '')}
      options={ENTITY_TYPE_OPTIONS}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Entity type"
          placeholder="Select entity type"
        />
      )}
      value={ENTITY_TYPE_OPTIONS.find((opt) => opt.value === value) || null}
    />
  </QuestionWrapper>
);

EntityTypeField.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired
};

// Business Industry Field
export const BusinessIndustryField = ({ value, subtype, onChange, onSubtypeChange }) => {
  const selectedCategory = INDUSTRY_CATEGORY_LIST.find((opt) => opt.value === value);
  const subtypeOptions = value ? INDUSTRY_SUBCATEGORY_MAP[value] || [] : [];

  return (
    <QuestionWrapper
      helperText="Please select the option that best describes your primary business activity."
      questionText="What is your business's industry type?"
    >
      <Stack spacing={3}>
        <Autocomplete
          getOptionLabel={(option) => option.label || ''}
          onChange={(event, newValue) => {
            onChange(newValue?.value || '');
            onSubtypeChange('');
          }}
          options={INDUSTRY_CATEGORY_LIST}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select industry category"
              placeholder="Select industry category"
            />
          )}
          value={selectedCategory || null}
        />
        {subtypeOptions.length > 0 && (
          <Autocomplete
            getOptionLabel={(option) => option.label || ''}
            onChange={(event, newValue) => onSubtypeChange(newValue?.value || '')}
            options={subtypeOptions}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select industry subcategory"
                placeholder="Select industry subcategory"
              />
            )}
            value={subtypeOptions.find((opt) => opt.value === subtype) || null}
          />
        )}
      </Stack>
    </QuestionWrapper>
  );
};

BusinessIndustryField.propTypes = {
  value: PropTypes.string,
  subtype: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onSubtypeChange: PropTypes.func.isRequired
};

// Business Description Field
export const BusinessDescriptionField = ({ value, onChange }) => (
  <QuestionWrapper
    helperText="Include your main products or services and who your typical customers are."
    questionText="Please briefly describe what your business does."
  >
    <TextField
      fullWidth
      inputProps={{ maxLength: UNIT_TEXT_DETAIL_MAX_LENGTH }}
      label="Enter details"
      multiline
      onChange={(e) => onChange(e.target.value)}
      placeholder="e.g. 'We provide marketing consulting to startups and small businesses in Chicago.'"
      rows={4}
      value={value}
    />
  </QuestionWrapper>
);

BusinessDescriptionField.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired
};

// Source of Funds Field
export const SourceOfFundsField = ({ value, valueDescription, onChange, onDescriptionChange }) => {
  const requiresDescription = value === 'ImportExportRevenue' || value === 'DonationsOrFundraising';

  return (
    <QuestionWrapper
      helperText="Select the option that best describes where your business funds come from."
      questionText="What is the primary source of funds for your business?"
    >
      <Stack spacing={3}>
        <Autocomplete
          getOptionLabel={(option) => option.label || ''}
          onChange={(event, newValue) => onChange(newValue?.value || '')}
          options={SOURCE_OF_FUNDS_OPTIONS}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select source of funds"
              placeholder="Select source of funds"
            />
          )}
          value={SOURCE_OF_FUNDS_OPTIONS.find((opt) => opt.value === value) || null}
        />
        {requiresDescription && (
          <TextField
            fullWidth
            inputProps={{ maxLength: UNIT_TEXT_DETAIL_MAX_LENGTH }}
            label="Please explain more detail around source of funds selection"
            multiline
            onChange={(e) => onDescriptionChange(e.target.value)}
            placeholder="Enter details"
            rows={3}
            value={valueDescription}
          />
        )}
      </Stack>
    </QuestionWrapper>
  );
};

SourceOfFundsField.propTypes = {
  value: PropTypes.string,
  valueDescription: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onDescriptionChange: PropTypes.func.isRequired
};

// Account Purpose Field
export const AccountPurposeField = ({ value, valueDetail, onChange, onDetailChange }) => (
  <QuestionWrapper
    helperText="Please select the main reason your business is opening this account."
    questionText="What is the primary purpose of this account?"
  >
    <Stack spacing={3}>
      <Autocomplete
        getOptionLabel={(option) => option.label || ''}
        onChange={(event, newValue) => onChange(newValue?.value || '')}
        options={ACCOUNT_PURPOSE_OPTIONS}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Select"
            placeholder="Select purpose"
          />
        )}
        value={ACCOUNT_PURPOSE_OPTIONS.find((opt) => opt.value === value) || null}
      />
      <TextField
        fullWidth
        inputProps={{ maxLength: UNIT_TEXT_DETAIL_MAX_LENGTH }}
        label="Please explain more detail around primary purpose of account."
        multiline
        onChange={(e) => onDetailChange(e.target.value)}
        placeholder="Enter details"
        rows={3}
        value={valueDetail}
      />
    </Stack>
  </QuestionWrapper>
);

AccountPurposeField.propTypes = {
  value: PropTypes.string,
  valueDetail: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onDetailChange: PropTypes.func.isRequired
};

// Transaction Volume Field
export const TransactionVolumeField = ({
  value,
  valueDescription,
  onChange,
  onDescriptionChange
}) => {
  const requiresDescription = value === 'Between1MAnd2M' || value === 'GreaterThan2M';

  return (
    <QuestionWrapper questionText="What is your expected monthly transactions volume for this account?">
      <Stack spacing={3}>
        <Autocomplete
          getOptionLabel={(option) => option.label || ''}
          onChange={(event, newValue) => onChange(newValue?.value || '')}
          options={TRANSACTION_VOLUME_OPTIONS}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select"
              placeholder="Select volume"
            />
          )}
          value={TRANSACTION_VOLUME_OPTIONS.find((opt) => opt.value === value) || null}
        />
        {requiresDescription && (
          <TextField
            fullWidth
            inputProps={{ maxLength: UNIT_TEXT_DETAIL_MAX_LENGTH }}
            label="Please explain more detail around transaction volume selection."
            multiline
            onChange={(e) => onDescriptionChange(e.target.value)}
            placeholder="Enter details"
            rows={3}
            value={valueDescription}
          />
        )}
      </Stack>
    </QuestionWrapper>
  );
};

TransactionVolumeField.propTypes = {
  value: PropTypes.string,
  valueDescription: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onDescriptionChange: PropTypes.func.isRequired
};

// Regulated Status Field
export const RegulatedStatusField = ({ value, regulatorName, onChange, onRegulatorNameChange }) => (
  <QuestionWrapper questionText="Is your business regulated by any government authority or financial regulator?">
    <Stack spacing={1.5}>
      <FormControl>
        <RadioGroup
          onChange={(e) => onChange(e.target.value === 'true')}
          value={value === null ? '' : String(value)}
        >
          <Stack spacing={1.5}>
            <RadioCardStyled
              control={<Radio />}
              label={
                <Text
                  size="xl"
                  weight="medium"
                >
                  Yes
                </Text>
              }
              selected={value === true}
              value="true"
            />
            <RadioCardStyled
              control={<Radio />}
              label={
                <Text
                  size="xl"
                  weight="medium"
                >
                  No
                </Text>
              }
              selected={value === false}
              value="false"
            />
          </Stack>
        </RadioGroup>
      </FormControl>
      {value === true && (
        <TextField
          fullWidth
          label="Please provide the name of your regulator."
          onChange={(e) => onRegulatorNameChange(e.target.value)}
          placeholder="Enter regulator name"
          value={regulatorName}
        />
      )}
    </Stack>
  </QuestionWrapper>
);

RegulatedStatusField.propTypes = {
  value: PropTypes.bool,
  regulatorName: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onRegulatorNameChange: PropTypes.func.isRequired
};

// US Nexus Field
export const USNexusField = ({ value, valueDetails, onChange, onDetailsChange }) => {
  const selectedValue = value === null ? true : value;

  const handleCheckboxChange = (optionValue) => (event) => {
    const newDetails = event.target.checked
      ? [...valueDetails, optionValue]
      : valueDetails.filter((v) => v !== optionValue);
    onDetailsChange(newDetails);
  };

  return (
    <QuestionWrapper
      helperText="e.g., employees, customers, physical locations, or banking relationships"
      questionText="Does your business have operational ties to the United States?"
    >
      <Stack spacing={1.5}>
        <FormControl>
          <RadioGroup
            onChange={(e) => {
              onChange(e.target.value === 'true');
              if (e.target.value === 'false') {
                onDetailsChange([]);
              }
            }}
            value={String(selectedValue)}
          >
            <Stack spacing={1.5}>
              <RadioCardStyled
                control={<Radio />}
                label={
                  <Text
                    size="xl"
                    weight="medium"
                  >
                    Yes
                  </Text>
                }
                selected={selectedValue === true}
                value="true"
              />
              <RadioCardStyled
                control={<Radio />}
                label={
                  <Stack spacing={0.5}>
                    <Text
                      size="xl"
                      weight="medium"
                    >
                      No
                    </Text>
                    {selectedValue === false && (
                      <Text
                        color="tertiary"
                        size="md"
                      >
                        ZenBusiness Banking requires operational ties to the United States. Your
                        application may be denied.
                      </Text>
                    )}
                  </Stack>
                }
                selected={selectedValue === false}
                value="false"
              />
            </Stack>
          </RadioGroup>
        </FormControl>
        {selectedValue === true && (
          <Stack
            spacing={1}
            sx={(theme) => ({
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.border.main}`,
              borderRadius: '12px',
              padding: 2
            })}
          >
            <QuestionWrapper questionText="Please specify the nature of your U.S. operations (select all that apply)." />
            {US_NEXUS_OPTIONS.map((option) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={valueDetails.includes(option.value)}
                    onChange={handleCheckboxChange(option.value)}
                  />
                }
                key={option.value}
                label={<Text size="md">{option.label}</Text>}
              />
            ))}
          </Stack>
        )}
      </Stack>
    </QuestionWrapper>
  );
};

USNexusField.propTypes = {
  value: PropTypes.bool,
  valueDetails: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
  onDetailsChange: PropTypes.func.isRequired
};
